import { useEffect, useRef, useState } from "preact/hooks";

interface EmailDocumentFrameProps {
  src: string;
  title: string;
  className?: string;
  fallbackParagraphs?: string[];
}

const MINIMUM_HEIGHT = 220;
const MAXIMUM_HEIGHT = 24_000;
const BODY_POLL_INTERVAL_MS = 50;
const EMPTY_BODY_LABELS = new Set([
  "(mensagem sem conteúdo)",
  "(mensagem sem conteúdo em texto)",
]);

export function EmailDocumentFrame({ src, title, className = "", fallbackParagraphs = [] }: EmailDocumentFrameProps) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const measureTimersRef = useRef<number[]>([]);
  const revealTimerRef = useRef<number | null>(null);
  const revealedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [height, setHeight] = useState(MINIMUM_HEIGHT);
  const previewParagraphs = fallbackParagraphs
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph && !EMPTY_BODY_LABELS.has(paragraph.toLocaleLowerCase("pt-BR")));
  const hasPreview = previewParagraphs.length > 0;

  useEffect(() => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    measureTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    measureTimersRef.current = [];
    if (revealTimerRef.current !== null) window.clearInterval(revealTimerRef.current);
    revealTimerRef.current = null;
    revealedRef.current = false;
    setLoading(true);
    setFailed(false);
    setHeight(MINIMUM_HEIGHT);

    revealTimerRef.current = window.setInterval(() => {
      revealDocument(false);
    }, BODY_POLL_INTERVAL_MS);

    return () => {
      observerRef.current?.disconnect();
      measureTimersRef.current.forEach((timer) => window.clearTimeout(timer));
      measureTimersRef.current = [];
      if (revealTimerRef.current !== null) window.clearInterval(revealTimerRef.current);
      revealTimerRef.current = null;
    };
  }, [src]);

  const syncHeight = () => {
    const document = frameRef.current?.contentDocument;
    if (!document) return;
    const body = document.body;
    if (!body) return;
    const nextHeight = Math.max(body.scrollHeight, body.offsetHeight, Math.ceil(body.getBoundingClientRect().height), MINIMUM_HEIGHT);
    setHeight((current) => {
      const measured = Math.min(nextHeight, MAXIMUM_HEIGHT);
      return Math.abs(current - measured) > 1 ? measured : current;
    });
  };

  const revealDocument = (loaded: boolean) => {
    if (revealedRef.current) {
      if (loaded) syncHeight();
      return;
    }

    let document: Document | null = null;
    try {
      document = frameRef.current?.contentDocument ?? null;
      if (!document?.body || document.URL === "about:blank") return;
      if (!loaded && document.readyState === "loading" && document.body.childNodes.length === 0) return;
    } catch {
      return;
    }

    revealedRef.current = true;
    if (revealTimerRef.current !== null) window.clearInterval(revealTimerRef.current);
    revealTimerRef.current = null;
    setLoading(false);
    setFailed(false);
    document?.documentElement.style.setProperty("overflow", "hidden", "important");
    document?.documentElement.style.setProperty("min-height", "0", "important");
    document?.body.style.setProperty("overflow", "hidden", "important");
    document?.body.style.setProperty("min-height", "0", "important");
    document?.body.style.setProperty("height", "auto", "important");
    syncHeight();
    observerRef.current?.disconnect();
    if (!document || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(syncHeight);
    if (document.documentElement) observer.observe(document.documentElement);
    if (document.body) observer.observe(document.body);
    observerRef.current = observer;
    measureTimersRef.current = [100, 500, 1_500].map((delay) => window.setTimeout(syncHeight, delay));
  };

  const handleLoad = () => revealDocument(true);

  const handleError = () => {
    if (revealTimerRef.current !== null) window.clearInterval(revealTimerRef.current);
    revealTimerRef.current = null;
    setLoading(false);
    setFailed(true);
  };

  return (
    <div
      className={`email-document${loading ? " is-loading" : ""}${failed ? " is-error" : ""}${className ? ` ${className}` : ""}`}
      aria-busy={loading}
    >
      {(loading || failed) && hasPreview ? (
        <div className="email-document__preview">
          {previewParagraphs.map((paragraph, index) => <p key={`${index}-${paragraph.slice(0, 24)}`}>{paragraph}</p>)}
          {loading ? <span className="email-document__format-status" role="status"><i aria-hidden="true" />Abrindo conteúdo formatado…</span> : null}
        </div>
      ) : null}
      {loading && !hasPreview ? (
        <div className="email-document__skeleton" role="status" aria-label="Abrindo conteúdo da mensagem">
          <i /><i /><i /><i /><i /><i />
        </div>
      ) : null}
      {failed ? <span className="email-document__error">O conteúdo formatado não abriu. Exibindo a versão em texto.</span> : null}
      <iframe
        ref={frameRef}
        className={loading || failed ? "is-pending" : "is-ready"}
        src={src}
        title={title}
        height={height}
        scrolling="no"
        loading="eager"
        referrerPolicy="no-referrer"
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
}
